import { colorFields, eyebrowField } from "./_shared";
/**
 * Newsletter — an email-capture form. Five layout variants. For now this is a
 * UI-only section: the form renders and shows a success state but does not
 * submit anywhere (no backend endpoint yet). The submit button uses the
 * section's button styling via the shared `data-section-btn-style` hook.
 */
export const newsletterSchema = {
    type: "newsletter",
    name: { ar: "نشرة بريدية", en: "Newsletter" },
    description: {
        ar: "فورم لتجميع إيميلات العملاء.",
        en: "An email capture form.",
    },
    maxPerTemplate: 2,
    settings: [
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "نوع العرض", en: "Layout" },
            helpText: { ar: "طريقة عرض الفورم.", en: "How the form is laid out." },
            options: [
                { value: "news-center", label: { ar: "متوسط", en: "Centered" } },
                { value: "news-split", label: { ar: "مقسّم", en: "Split" } },
                { value: "news-band", label: { ar: "شريط", en: "Band" } },
                { value: "news-card", label: { ar: "بطاقة", en: "Card" } },
                { value: "news-minimal", label: { ar: "بسيط", en: "Minimal" } },
                // Ported from EasyOrders `newsletter-immersive` (elite family): the
                // form centred on a full-bleed photograph behind a dimming overlay,
                // with an optional fine-print line under it. A variant rather than a
                // new section — same content model plus a background image.
                { value: "news-immersive", label: { ar: "صورة كاملة", en: "Immersive" } },
            ],
            default: "news-center",
        },
        // A5: the minimal variant renders only the title + form, so the eyebrow and
        // subhead are dead there.
        ...eyebrowField({
            variant: [
                "news-center",
                "news-split",
                "news-band",
                "news-card",
                "news-immersive",
            ],
        }),
        {
            id: "title",
            type: "text",
            i18n: true,
            label: { ar: "العنوان", en: "Title" },
            validate: { max: 80 },
            default: { ar: "اشترك في نشرتنا", en: "Join our newsletter" },
        },
        {
            id: "subhead",
            type: "textarea",
            i18n: true,
            label: { ar: "وصف (اختياري)", en: "Subhead (optional)" },
            validate: { max: 200 },
            showIf: {
                variant: [
                    "news-center",
                    "news-split",
                    "news-band",
                    "news-card",
                    "news-immersive",
                ],
            },
        },
        {
            id: "placeholder",
            type: "text",
            i18n: true,
            label: { ar: "النص داخل خانة الإيميل", en: "Email placeholder" },
            validate: { max: 60 },
            default: { ar: "اكتب إيميلك", en: "Enter your email" },
        },
        {
            id: "buttonLabel",
            type: "text",
            i18n: true,
            label: { ar: "نص الزر", en: "Button label" },
            validate: { max: 24 },
            default: { ar: "اشترك", en: "Subscribe" },
        },
        {
            id: "successMessage",
            type: "text",
            i18n: true,
            label: { ar: "رسالة النجاح", en: "Success message" },
            validate: { max: 120 },
            default: { ar: "شكرًا لاشتراكك!", en: "Thanks for subscribing!" },
        },
        // ---- news-immersive only ------------------------------------------------
        // Driven on their side by `--ni-*` custom properties: a desktop + mobile
        // background image, an overlay alpha, a height modifier and a fine-print
        // line. Scoped to the variant so the other five never show them.
        {
            id: "image",
            type: "image",
            label: { ar: "صورة الخلفية", en: "Background image" },
            helpText: {
                ar: "الصورة اللي ورا الفورم. بتتقص لملء العرض.",
                en: "The photograph behind the form. Cropped to fill.",
            },
            focalPoint: true,
            showIf: { variant: ["news-immersive"] },
        },
        {
            id: "overlayOpacity",
            type: "range",
            label: { ar: "تعتيم الصورة", en: "Image dimming" },
            helpText: {
                ar: "زوّده لو النص مش واضح على الصورة.",
                en: "Raise this if the copy is hard to read over the photo.",
            },
            min: 0,
            max: 90,
            step: 1,
            default: 45,
            showIf: { variant: ["news-immersive"] },
        },
        {
            id: "height",
            type: "select",
            label: { ar: "الارتفاع", en: "Height" },
            options: [
                { value: "compact", label: { ar: "قصير", en: "Compact" } },
                { value: "comfortable", label: { ar: "متوسط", en: "Comfortable" } },
                { value: "tall", label: { ar: "طويل", en: "Tall" } },
            ],
            default: "comfortable",
            showIf: { variant: ["news-immersive"] },
        },
        {
            id: "fineprint",
            type: "textarea",
            i18n: true,
            label: { ar: "سطر صغير تحت الفورم", en: "Fine print (optional)" },
            helpText: {
                ar: "مثلاً: بالاشتراك أنت موافق على استلام رسائل تسويقية.",
                en: 'e.g. "By subscribing you agree to receive marketing emails."',
            },
            validate: { max: 160 },
            showIf: { variant: ["news-immersive"] },
        },
        ...colorFields(),
        {
            id: "buttonStyle",
            type: "select",
            label: { ar: "نمط الزر", en: "Button style" },
            helpText: {
                ar: "بيتطبق على زر الاشتراك.",
                en: "Applied to the subscribe button.",
            },
            options: [
                { value: "primary", label: { ar: "أساسي", en: "Primary" } },
                { value: "outline", label: { ar: "إطار", en: "Outline" } },
                { value: "ghost", label: { ar: "شفاف", en: "Ghost" } },
                { value: "link", label: { ar: "رابط", en: "Link" } },
            ],
            default: "primary",
        },
    ],
};
//# sourceMappingURL=newsletter.js.map